/* eslint-disable no-console, class-methods-use-this, no-new, default-case, no-plusplus, prefer-exponentiation-operator, prettier/prettier */
import 'webrtc-adapter';
import {
  UserAgent,
  Registerer,
  Inviter,
  SessionState,
  RegistererState,
} from 'sip.js';
import ringtoneFile from 'dashboard/assets/audio/ringtone.mp3';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  createTickWorker,
  bindWakeEvents,
} from '../telephony_shared/sipResilience';

/**
 * SIP over WebSocket and WebRTC client for WhatsApp calling via Asterisk.
 * Full-featured: hold, blind transfer, attended transfer.
 *
 * @see https://sipjs.com/api/0.20.0/
 */
export default class WhatsAppSipClient {
  constructor(params = {}) {
    this.params = {
      extension: null,
      password: null,
      displayName: null,
      sipServer: null,
      wsServer: null,
      debug: true,
      onUserAgentAction: () => {},
      onCallEvent: () => {},
      ...params,
    };

    // Call state
    this.activeCall = null;
    this.direction = null;
    this.startTime = null;
    this.currentDestination = null;
    this.iceConnectionState = 'new';
    this.lastCallEndedAt = 0;
    this.localHold = false;

    // Consultation (attended transfer) state
    this.consultationCall = null;
    this.consultationAudioElement = null;
    this.isAttendedTransferInProgress = false;
    this.transferCleanupTimeout = null;

    // Audio state
    this.audioPlayerElement = null;
    this.micMuted = false;
    this.outputVolume = 1;
    this.audioUnlocked = false;

    // Ringtone setup (incoming calls)
    this.ringtone = new Audio(ringtoneFile);
    this.ringtone.loop = true;
    this.ringtone.volume = 0.8;

    // Ringback tone (outgoing calls) — synthesized via Web Audio API
    // Brazilian standard: 425Hz, 1s on / 4s off
    this.ringbackCtx = null;
    this.ringbackTimer = null;

    // Registration state
    this.isRegistered = false;
    this.onCallAction = () => {};
    this.onUserAgentAction = this.params.onUserAgentAction;

    // Auto-reconnect (sem teto de tentativas — backoff capado em 60s; os
    // wake events furam a fila quando aba/rede/máquina voltam)
    this.reconnectAttempts = 0;
    this.reconnectDelay = 3000;
    this.reconnectTimer = null;
    this.intentionallyStopped = false;
    this.isStarting = false;

    // Keep-alive (WebSocket ping) — tick num Web Worker, não em setInterval
    // de página: timers de página são throttled em aba background e o contato
    // expirava no Asterisk. Ver telephony_shared/sipResilience.js.
    this.keepAliveWorker = null;
    this.keepAliveIntervalMs = 25000;
    // Re-REGISTER proativo a cada N ticks (12 × 25s = 300s, metade do
    // expires 600) — o refresh interno do SIP.js usa timer de página e
    // atrasa/congela em background.
    this.registerRefreshEveryTicks = 12;
    this.ticksSinceRegister = 0;
    this.unbindWakeEvents = null;

    // SIP.js User Agent
    const sipUri = `sip:${this.params.extension}@${this.params.sipServer}`;
    console.log('[WhatsAppSipClient] Creating UserAgent:', { sipUri, wsServer: this.params.wsServer, debug: this.params.debug });

    this.sipUserAgent = new UserAgent({
      uri: UserAgent.makeURI(sipUri),
      transportOptions: {
        server: this.params.wsServer,
        traceSip: this.params.debug,
      },
      authorizationUsername: this.params.extension,
      authorizationPassword: this.params.password,
      displayName: this.params.displayName || this.params.extension,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
        },
      },
      delegate: {
        onInvite: invitation => this.handleIncomingCall(invitation),
        onDisconnect: error => this.handleDisconnect(error),
        onConnect: () => this.handleConnect(),
      },
      logLevel: this.params.debug ? 'debug' : 'error',
    });

    // Create Registerer
    this.registerer = new Registerer(this.sipUserAgent, {
      expires: 600,
    });

    this.setupRegistererEvents();

    // Reconexão orientada a evento (rede voltou / aba visível / resume) —
    // por último: se a construção do UserAgent lançar, nenhum listener global
    // fica preso a um objeto meio-construído.
    this.unbindWakeEvents = bindWakeEvents(reason => this.handleWake(reason));
  }

  // ==================== RINGTONE ====================

  unlockAudio() {
    if (this.audioUnlocked) return;

    const unlockPromise = this.ringtone.play();
    if (unlockPromise) {
      unlockPromise
        .then(() => {
          this.ringtone.pause();
          this.ringtone.currentTime = 0;
          this.audioUnlocked = true;
        })
        .catch(() => {
          // Will retry on next interaction
        });
    }
  }

  playRingtone() {
    if (!this.audioUnlocked) {
      console.warn('[WhatsAppSipClient] Audio not unlocked — ringtone will not play. Click the page first.');
      this.showIncomingCallNotification();
      return;
    }

    this.ringtone.play().catch(err => {
      console.error('[WhatsAppSipClient] Error playing ringtone:', err);
      this.showIncomingCallNotification();
    });
  }

  stopRingtone() {
    this.ringtone.pause();
    this.ringtone.currentTime = 0;
  }

  playRingback() {
    this.stopRingback();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.ringbackCtx = ctx;

      const playTone = () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 425;
        gain.gain.value = 0.3;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 1);
      };

      playTone();
      this.ringbackTimer = setInterval(playTone, 5000);
    } catch (err) {
      console.warn('[WhatsAppSipClient] Ringback tone failed:', err);
    }
  }

  stopRingback() {
    if (this.ringbackTimer) {
      clearInterval(this.ringbackTimer);
      this.ringbackTimer = null;
    }
    if (this.ringbackCtx) {
      this.ringbackCtx.close().catch(() => {});
      this.ringbackCtx = null;
    }
  }

  showIncomingCallNotification() {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('WhatsApp Call', {
        body: 'Incoming WhatsApp call',
        icon: '/favicon.ico',
        requireInteraction: true,
        tag: 'whatsapp-incoming-call',
      });
    }
  }

  // ==================== SETUP ====================

  setupRegistererEvents() {
    this.registerer.stateChange.addListener(state => {
      console.log('[WhatsAppSipClient] Registerer state changed:', state);
      switch (state) {
        case RegistererState.Registered:
          console.log('[WhatsAppSipClient] ✅ SIP REGISTERED');
          this.isRegistered = true;
          this.reconnectAttempts = 0;
          this.startKeepAlive();
          this.onUserAgentAction({ type: 'registered' });
          break;

        case RegistererState.Unregistered:
          console.warn('[WhatsAppSipClient] SIP UNREGISTERED');
          this.isRegistered = false;
          this.stopKeepAlive();
          this.onUserAgentAction({ type: 'unregistered' });
          break;

        case RegistererState.Terminated:
          console.error('[WhatsAppSipClient] SIP TERMINATED');
          this.isRegistered = false;
          this.stopKeepAlive();
          this.onUserAgentAction({ type: 'unregistered' });
          this.scheduleReconnectIfNeeded();
          break;
      }
    });
  }

  // ==================== KEEP-ALIVE ====================

  startKeepAlive() {
    this.stopKeepAlive();
    this.ticksSinceRegister = 0;

    this.keepAliveWorker = createTickWorker(this.keepAliveIntervalMs, () =>
      this.handleKeepAliveTick()
    );
  }

  handleKeepAliveTick() {
    if (!this.sipUserAgent || !this.sipUserAgent.transport) return;
    try {
      this.sipUserAgent.transport.send('\r\n\r\n');
    } catch (error) {
      console.error('[WhatsAppSipClient] Keep-alive error:', error);
    }

    this.ticksSinceRegister++;
    if (this.ticksSinceRegister >= this.registerRefreshEveryTicks) {
      this.ticksSinceRegister = 0;
      this.refreshRegistration();
    }
  }

  // REGISTER de renovação fora do agendador interno do SIP.js (que roda em
  // timer de página e atrasa em background). Idempotente: refresh só reseta
  // o expires do contato no Asterisk.
  refreshRegistration() {
    if (!this.isRegistered) return;
    try {
      this.registerer.register().catch(error => {
        console.warn('[WhatsAppSipClient] Register refresh failed:', error.message);
      });
    } catch (error) {
      // register() lança se já há um REGISTER em voo — o próximo tick renova
      console.warn('[WhatsAppSipClient] Register refresh skipped:', error.message);
    }
  }

  stopKeepAlive() {
    if (this.keepAliveWorker) {
      this.keepAliveWorker.stop();
      this.keepAliveWorker = null;
    }
  }

  handleConnect() {
    console.log('[WhatsAppSipClient] ✅ WebSocket CONNECTED to', this.params.wsServer);
    this.onUserAgentAction({ type: 'connected' });
  }

  handleDisconnect(error) {
    console.error('[WhatsAppSipClient] ❌ WebSocket DISCONNECTED', error);
    this.isRegistered = false;
    this.stopKeepAlive();
    this.onUserAgentAction({ type: 'disconnected', payload: { error } });
    this.scheduleReconnectIfNeeded();
  }

  // ==================== CONNECTION ====================

  async start() {
    // Guard de concorrência: wake events (focus + visibilitychange + pageshow
    // chegam juntos) e o backoff podem disparar start() em paralelo.
    if (this.isStarting) return;
    this.isStarting = true;
    this.intentionallyStopped = false;
    console.log('[WhatsAppSipClient] start() — connecting to', this.params.wsServer);

    try {
      console.log('[WhatsAppSipClient] Calling sipUserAgent.start()...');
      await this.sipUserAgent.start();
      console.log('[WhatsAppSipClient] sipUserAgent started, calling registerer.register()...');
      await this.registerer.register();
      console.log('[WhatsAppSipClient] registerer.register() completed');
    } catch (error) {
      console.error('[WhatsAppSipClient] Error starting:', error.message, error);
      this.onUserAgentAction({ type: 'registrationFailed', payload: { error } });
      this.scheduleReconnectIfNeeded();
    } finally {
      this.isStarting = false;
    }
  }

  async stop() {
    this.intentionallyStopped = true;
    this.clearReconnectTimer();
    this.stopKeepAlive();
    if (this.unbindWakeEvents) {
      this.unbindWakeEvents();
      this.unbindWakeEvents = null;
    }

    try {
      if (this.isRegistered) {
        await this.registerer.unregister();
      }
      await this.sipUserAgent.stop();
    } catch (error) {
      console.error('[WhatsAppSipClient] Error stopping:', error);
    }
  }

  // ==================== RECONNECT ====================

  scheduleReconnectIfNeeded() {
    if (this.intentionallyStopped) return;

    this.clearReconnectTimer();

    // Sem teto de tentativas: desistir deixava o telefone morto até F5
    // (laptop que dormiu >10min). Backoff exponencial capado em 60s; o
    // expoente é clampado pra não estourar pra Infinity.
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 10)),
      60000
    );

    this.reconnectTimer = setTimeout(() => this.attemptReconnect(), delay);
  }

  // Aba/rede/máquina voltou: reconecta na hora (sem esperar o backoff, cujo
  // timer pode ter sido congelado durante o sleep). Se ainda achamos que
  // estamos registrados, o socket pode ser um zumbi pós-sleep — um REGISTER
  // imediato ou confirma o contato ou derruba o transport (e o
  // handleDisconnect agenda a reconexão).
  handleWake(reason) {
    if (this.intentionallyStopped) return;

    if (this.isRegistered) {
      this.refreshRegistration();
      return;
    }

    console.log('[WhatsAppSipClient] Wake event:', reason, '— reconnecting now');
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.attemptReconnect();
  }

  async attemptReconnect() {
    if (this.isRegistered) {
      this.reconnectAttempts = 0;
      return;
    }

    try {
      await this.start();
    } catch (error) {
      console.error('[WhatsAppSipClient] Reconnect error:', error.message);
      this.scheduleReconnectIfNeeded();
    }
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ==================== INCOMING CALL ====================

  handleIncomingCall(invitation) {
    const INCOMING_COOLDOWN_MS = 2000;
    if (this.activeCall || (Date.now() - this.lastCallEndedAt < INCOMING_COOLDOWN_MS)) {
      invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' });
      return;
    }

    this.activeCall = invitation;
    this.direction = 'incoming';
    this.startTime = null;

    this.setupSessionEvents(invitation);
    this.playRingtone();

    const callerNumber = invitation.remoteIdentity?.uri?.user || 'unknown';
    const callerDisplayName = invitation.remoteIdentity?.displayName || null;

    this.onUserAgentAction({
      type: 'newRTCSession',
      payload: {
        session: {
          direction: 'incoming',
          remote_identity: {
            uri: { user: callerNumber },
            display_name: callerDisplayName,
          },
        },
      },
    });

    emitter.emit(BUS_EVENTS.WHATSAPP_SIP_INCOMING_CALL, {
      number: callerNumber,
      displayName: callerDisplayName,
    });

    this.onCallAction({
      type: 'incoming',
      from: callerNumber,
      displayName: callerDisplayName,
    });
  }

  // ==================== CALL ====================

  call(options = {}) {
    if (!this.isRegistered) {
      throw new Error('SIP not registered');
    }

    if (this.activeCall) {
      throw new Error('A call is already active');
    }

    const { to, onCallAction = () => {}, inboxId } = options;

    if (!to) {
      throw new Error('Destination number not provided');
    }

    this.onCallAction = onCallAction;
    this.currentDestination = to;

    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    const cleanNumber = to.replace(/[^\d+]/g, '');
    const targetUri = UserAgent.makeURI(`sip:${cleanNumber}@${this.params.sipServer}`);

    const extraHeaders = [];
    if (inboxId) {
      extraHeaders.push(`X-Inbox-Id: ${inboxId}`);
    }

    const inviter = new Inviter(this.sipUserAgent, targetUri, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      extraHeaders,
    });

    this.activeCall = inviter;
    this.direction = 'outgoing';
    this.startTime = null;

    this.setupSessionEvents(inviter);

    inviter
      .invite({
        requestDelegate: {
          // Resposta final negativa do INVITE (ex.: 404 quando o número não
          // casa com nenhum padrão no contexto from-agents = número inválido/
          // sem DDD). Emitimos um evento dedicado com o status code pro toast
          // mostrar uma mensagem clara; o Terminated logo em seguida só limpa.
          onReject: response => {
            const statusCode = response?.message?.statusCode;
            this.emitCallEvent({
              type: 'rejected',
              payload: { statusCode, number: cleanNumber },
            });
          },
        },
      })
      .catch(error => {
        console.error('[WhatsAppSipClient] Error making call:', error);
        this.emitCallEvent({ type: 'failed', payload: { cause: error.message } });
        this.cleanupSession();
      });

    this.onUserAgentAction({
      type: 'newRTCSession',
      payload: {
        session: {
          direction: 'outgoing',
          remote_identity: {
            uri: { user: cleanNumber },
          },
        },
      },
    });
  }

  answer() {
    if (!this.activeCall) {
      throw new Error('No call to answer');
    }

    this.stopRingtone();

    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    this.activeCall.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    }).catch(err => {
      console.error('[WhatsAppSipClient] Error answering:', err);
      throw err;
    });
  }

  hangup() {
    this.stopRingtone();

    if (!this.activeCall) {
      console.warn('[WhatsAppSipClient] No active call to hang up');
      return;
    }

    try {
      const state = this.activeCall.state;

      if (state === SessionState.Initial || state === SessionState.Establishing) {
        if (this.direction === 'incoming') {
          this.activeCall.reject({ statusCode: 486, reasonPhrase: 'Busy Here' });
        } else {
          this.activeCall.cancel();
        }
      } else if (state === SessionState.Established) {
        this.activeCall.bye();
      }
    } catch (error) {
      console.error('[WhatsAppSipClient] Error hanging up:', error);
      this.cleanupSession();
    }
  }

  // ==================== SESSION EVENTS ====================

  setupSessionEvents(session) {
    session.stateChange.addListener(state => {
      switch (state) {
        case SessionState.Establishing:
          if (this.direction === 'outgoing') {
            this.playRingback();
          }
          this.emitCallEvent({ type: 'progress' });
          break;

        case SessionState.Established:
          this.stopRingtone();
          this.stopRingback();
          this.startTime = new Date();
          this.connectAudio();
          this.emitCallEvent({ type: 'accepted' });
          this.emitCallEvent({ type: 'confirmed' });
          break;

        case SessionState.Terminating:
          break;

        case SessionState.Terminated:
          this.stopRingtone();
          this.stopRingback();
          this.disconnectAudio();

          if (this.activeCall === session) {
            this.emitCallEvent({ type: 'ended' });
            this.cleanupSession();
          }
          break;
      }
    });

    const monitorPeerConnection = () => {
      const sdh = session.sessionDescriptionHandler;
      if (!sdh) return;

      const pc = sdh.peerConnection;
      if (!pc) return;

      pc.addEventListener('track', (event) => {
        if (this.activeCall === session && event.track.kind === 'audio') {
          this.connectAudio();
        }
      });

      pc.addEventListener('iceconnectionstatechange', () => {
        const iceState = pc.iceConnectionState;
        this.iceConnectionState = iceState;
        console.log('[WhatsAppSipClient] ICE connection state:', iceState);

        if ((iceState === 'connected' || iceState === 'completed') && this.activeCall === session) {
          this.connectAudio();
        }

        // When the remote side hangs up via ARI, the media path drops
        // and ICE transitions to disconnected/failed. Treat as call ended.
        if ((iceState === 'disconnected' || iceState === 'failed') && this.activeCall === session) {
          console.warn('[WhatsAppSipClient] ICE disconnected/failed — forcing call cleanup');
          this.stopRingtone();
          this.disconnectAudio();
          this.emitCallEvent({ type: 'ended' });
          this.cleanupSession();
        }
      });
    };

    if (session.sessionDescriptionHandler) {
      monitorPeerConnection();
    } else {
      session.stateChange.addListener((state) => {
        if (state === SessionState.Established) {
          setTimeout(() => monitorPeerConnection(), 100);
        }
      });
    }
  }

  emitCallEvent(event) {
    this.onCallAction(event);
    this.params.onCallEvent(event);
  }

  getActiveCall() {
    return this.activeCall;
  }

  cleanupSession() {
    this.stopRingtone();
    this.lastCallEndedAt = Date.now();

    this.activeCall = null;
    this.direction = null;
    this.startTime = null;
    this.currentDestination = null;
    this.iceConnectionState = 'new';
    this.localHold = false;
    this.isAttendedTransferInProgress = false;
    this.onCallAction = () => {};
  }

  // ==================== AUDIO ====================

  setAudioElement(domNode) {
    if (domNode === undefined) {
      this.audioPlayerElement = document.createElement('audio');
      document.body.appendChild(this.audioPlayerElement);
    } else if (typeof domNode === 'string') {
      const found = document.querySelector(domNode);
      if (!found || found.tagName.toLowerCase() !== 'audio') {
        throw new Error('Invalid DOM selector for audio element');
      }
      this.audioPlayerElement = found;
    } else {
      this.audioPlayerElement = domNode;
    }

    this.audioPlayerElement.defaultMuted = false;
    this.audioPlayerElement.autoplay = true;
    this.audioPlayerElement.volume = this.outputVolume;
  }

  connectAudio() {
    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    this.disconnectAudio();

    const sessionDescriptionHandler = this.activeCall?.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.error('[WhatsAppSipClient] Session description handler not available');
      return;
    }

    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      console.error('[WhatsAppSipClient] PeerConnection not available');
      return;
    }

    const remoteStream = new MediaStream();
    peerConnection.getReceivers().forEach(receiver => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (remoteStream.getTracks().length > 0) {
      this.audioPlayerElement.srcObject = remoteStream;
    }

    this.setMicMuted(this.micMuted);
  }

  disconnectAudio() {
    if (!this.audioPlayerElement?.srcObject) return;
    this.audioPlayerElement.srcObject = null;
  }

  setMicMuted(muted) {
    this.micMuted = muted;

    const sessionDescriptionHandler = this.activeCall?.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) return;

    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) return;

    peerConnection.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !muted;
      }
    });
  }

  getMicMuted() {
    return this.micMuted;
  }

  toggleMute() {
    this.setMicMuted(!this.micMuted);
    return this.micMuted;
  }

  // ==================== HOLD ====================

  async hold() {
    if (!this.activeCall) {
      throw new Error('No active call to hold');
    }

    if (this.localHold) {
      return false;
    }

    try {
      await this.activeCall.invite({
        sessionDescriptionHandlerModifiers: [
          description => {
            description.sdp = description.sdp.replace(/a=sendrecv/g, 'a=sendonly');
            return description;
          },
        ],
      });

      this.localHold = true;
      this.emitCallEvent({ type: 'hold' });
      return true;
    } catch (error) {
      console.error('[WhatsAppSipClient] Hold failed:', error);
      this.emitCallEvent({ type: 'hold_failed' });
      return false;
    }
  }

  async unhold() {
    if (!this.activeCall) {
      throw new Error('No active call to unhold');
    }

    if (!this.localHold) {
      return false;
    }

    try {
      await this.activeCall.invite({
        sessionDescriptionHandlerModifiers: [
          description => {
            description.sdp = description.sdp.replace(/a=sendonly/g, 'a=sendrecv');
            description.sdp = description.sdp.replace(/a=inactive/g, 'a=sendrecv');
            return description;
          },
        ],
      });

      this.localHold = false;
      this.emitCallEvent({ type: 'unhold' });
      return true;
    } catch (error) {
      console.error('[WhatsAppSipClient] Unhold failed:', error);
      this.emitCallEvent({ type: 'unhold_failed' });
      return false;
    }
  }

  isOnHold() {
    return this.localHold;
  }

  toggleHold() {
    if (!this.activeCall) return false;

    if (this.localHold) {
      this.unhold();
      return false;
    }
    this.hold();
    return true;
  }

  // ==================== TRANSFER ====================

  async transfer(target) {
    if (!this.activeCall) {
      throw new Error('No active call to transfer');
    }

    if (!target) {
      throw new Error('Transfer target not provided');
    }

    const cleanTarget = target.replace(/[^\d+]/g, '');
    const targetUri = UserAgent.makeURI(`sip:${cleanTarget}@${this.params.sipServer}`);

    try {
      const referOptions = {
        requestDelegate: {
          onAccept: () => {
            if (this.transferCleanupTimeout) {
              clearTimeout(this.transferCleanupTimeout);
              this.transferCleanupTimeout = null;
            }
            try {
              if (this.activeCall && this.activeCall.state === SessionState.Established) {
                this.activeCall.bye();
              }
            } catch (byeError) {
              console.warn('[WhatsAppSipClient] BYE after blind transfer error:', byeError);
              this.emitCallEvent({ type: 'ended' });
              this.cleanupSession();
            }
          },
          onReject: (response) => {
            console.error('[WhatsAppSipClient] Blind REFER rejected:', response.message.statusCode);
            this.emitCallEvent({ type: 'transfer_failed', target: cleanTarget });
          },
        },
        delegate: {
          onNotify: (notification) => {
            const body = notification.request.body || '';
            notification.accept();

            if (body.match(/SIP\/2\.0\s+2\d\d/)) {
              console.log('[WhatsAppSipClient] Blind transfer confirmed via NOTIFY');
              if (this.transferCleanupTimeout) {
                clearTimeout(this.transferCleanupTimeout);
                this.transferCleanupTimeout = null;
              }
              try {
                if (this.activeCall && this.activeCall.state === SessionState.Established) {
                  this.activeCall.bye();
                }
              } catch (byeError) {
                console.warn('[WhatsAppSipClient] BYE after transfer NOTIFY error:', byeError);
                this.emitCallEvent({ type: 'ended' });
                this.cleanupSession();
              }
            }
          },
        },
      };

      await this.activeCall.refer(targetUri, referOptions);
      this.emitCallEvent({ type: 'transfer_accepted', target: cleanTarget });

      // Fallback: if NOTIFY never arrives, send BYE after 10s
      this.transferCleanupTimeout = setTimeout(() => {
        console.warn('[WhatsAppSipClient] Blind transfer NOTIFY timeout, forcing BYE');
        try {
          if (this.activeCall && this.activeCall.state === SessionState.Established) {
            this.activeCall.bye();
          }
        } catch (e) {
          this.emitCallEvent({ type: 'ended' });
          this.cleanupSession();
        }
      }, 10000);
    } catch (error) {
      console.error('[WhatsAppSipClient] Transfer failed:', error);
      this.emitCallEvent({ type: 'transfer_failed', target: cleanTarget, error });
    }
  }

  // ==================== ATTENDED TRANSFER ====================

  async attendedTransfer(target) {
    if (!this.activeCall) {
      throw new Error('No active call to transfer');
    }

    if (!target) {
      throw new Error('Transfer target not provided');
    }

    if (this.consultationCall) {
      throw new Error('Consultation already in progress');
    }

    const cleanTarget = target.replace(/[^\d+]/g, '');
    const targetUri = UserAgent.makeURI(`sip:${cleanTarget}@${this.params.sipServer}`);

    try {
      // Step 1: Hold the original call
      if (!this.localHold) {
        const holdSuccess = await this.hold();
        if (!holdSuccess) {
          throw new Error('Failed to hold original call');
        }
      }

      this.isAttendedTransferInProgress = true;

      // Step 2: Create consultation audio element
      if (!this.consultationAudioElement) {
        this.consultationAudioElement = document.createElement('audio');
        this.consultationAudioElement.autoplay = true;
        this.consultationAudioElement.volume = this.outputVolume;
        document.body.appendChild(this.consultationAudioElement);
      }

      // Step 3: Create and send consultation INVITE
      const inviter = new Inviter(this.sipUserAgent, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });

      this.consultationCall = inviter;
      this.setupConsultationSessionEvents(inviter);

      this.emitCallEvent({ type: 'consultation_calling', target: cleanTarget });
      await inviter.invite();
    } catch (error) {
      console.error('[WhatsAppSipClient] Attended transfer failed:', error);
      this.emitCallEvent({ type: 'consultation_failed', error: error.message });
      this.cleanupConsultation();

      if (this.activeCall && this.localHold) {
        try {
          await this.unhold();
        } catch (unholdErr) {
          console.error('[WhatsAppSipClient] Unhold after consultation failure:', unholdErr);
        }
      }
    }
  }

  setupConsultationSessionEvents(session) {
    session.stateChange.addListener(state => {
      switch (state) {
        case SessionState.Establishing:
          this.emitCallEvent({ type: 'consultation_progress' });
          break;

        case SessionState.Established:
          this.connectConsultationAudio();
          this.emitCallEvent({ type: 'consultation_established' });
          break;

        case SessionState.Terminated:
          this.disconnectConsultationAudio();

          if (this.consultationCall === session) {
            if (this.isAttendedTransferInProgress && this.activeCall) {
              this.isAttendedTransferInProgress = false;
              this.cleanupConsultation();
              this.emitCallEvent({ type: 'consultation_ended' });

              if (this.localHold) {
                this.unhold().catch(err => {
                  console.error('[WhatsAppSipClient] Unhold after consultation ended:', err);
                });
              }
            } else {
              this.cleanupConsultation();
            }
          }
          break;
      }
    });
  }

  async completeAttendedTransfer() {
    if (!this.activeCall) {
      throw new Error('No original call active');
    }

    if (!this.consultationCall) {
      throw new Error('No consultation active to complete');
    }

    if (this.consultationCall.state !== SessionState.Established) {
      throw new Error('Consultation not connected');
    }

    try {
      const referOptions = {
        requestDelegate: {
          onAccept: () => {
            this.handleTransferSuccess();
          },
          onReject: (response) => {
            console.error('[WhatsAppSipClient] Attended REFER rejected:', response.message.statusCode);
            this.emitCallEvent({ type: 'attended_transfer_failed', error: 'REFER rejected' });
          },
        },
        delegate: {
          onNotify: (notification) => {
            const body = notification.request.body || '';
            notification.accept();

            if (body.match(/SIP\/2\.0\s+2\d\d/)) {
              console.log('[WhatsAppSipClient] Attended transfer confirmed via NOTIFY');
              this.handleTransferSuccess();
            } else if (body.match(/SIP\/2\.0\s+[4-6]\d\d/)) {
              console.warn('[WhatsAppSipClient] Attended transfer failed via NOTIFY:', body);
              this.handleTransferSuccess();
            }
          },
        },
      };

      await this.activeCall.refer(this.consultationCall, referOptions);
      this.isAttendedTransferInProgress = false;

      this.transferCleanupTimeout = setTimeout(() => {
        console.warn('[WhatsAppSipClient] Attended transfer NOTIFY timeout, forcing cleanup');
        this.handleTransferSuccess();
      }, 15000);

      this.emitCallEvent({ type: 'attended_transfer_completed' });
    } catch (error) {
      console.error('[WhatsAppSipClient] Complete attended transfer error:', error);
      this.emitCallEvent({ type: 'attended_transfer_failed', error: error.message });
    }
  }

  handleTransferSuccess() {
    if (this.transferCleanupTimeout) {
      clearTimeout(this.transferCleanupTimeout);
      this.transferCleanupTimeout = null;
    }

    this.disconnectAudio();
    this.disconnectConsultationAudio();

    try {
      if (this.consultationCall && this.consultationCall.state === SessionState.Established) {
        this.consultationCall.bye();
      }
    } catch (consultByeError) {
      console.warn('[WhatsAppSipClient] BYE consultation error:', consultByeError);
    }

    try {
      if (this.activeCall && this.activeCall.state === SessionState.Established) {
        this.activeCall.bye();
      }
    } catch (activeByeError) {
      console.warn('[WhatsAppSipClient] BYE active call error:', activeByeError);
    }

    setTimeout(() => {
      this.cleanupConsultation();
      this.emitCallEvent({ type: 'ended' });
      this.cleanupSession();
    }, 500);
  }

  async cancelAttendedTransfer() {
    this.isAttendedTransferInProgress = false;

    if (this.consultationCall) {
      try {
        const consultState = this.consultationCall.state;
        if (consultState === SessionState.Initial || consultState === SessionState.Establishing) {
          this.consultationCall.cancel();
        } else if (consultState === SessionState.Established) {
          this.consultationCall.bye();
        }
      } catch (err) {
        console.warn('[WhatsAppSipClient] Cancel consultation error:', err);
      }
    }

    this.cleanupConsultation();
    this.emitCallEvent({ type: 'consultation_cancelled' });

    if (this.activeCall && this.localHold) {
      try {
        await this.unhold();
      } catch (err) {
        console.error('[WhatsAppSipClient] Unhold after cancel error:', err);
      }
    }
  }

  // ==================== CONSULTATION AUDIO ====================

  connectConsultationAudio() {
    if (!this.consultationAudioElement) {
      this.consultationAudioElement = document.createElement('audio');
      this.consultationAudioElement.autoplay = true;
      this.consultationAudioElement.volume = this.outputVolume;
      document.body.appendChild(this.consultationAudioElement);
    }

    this.disconnectConsultationAudio();

    const sdh = this.consultationCall?.sessionDescriptionHandler;
    if (!sdh) {
      console.error('[WhatsAppSipClient] Consultation SDH not available');
      return;
    }

    const pc = sdh.peerConnection;
    if (!pc) {
      console.error('[WhatsAppSipClient] Consultation PeerConnection not available');
      return;
    }

    const remoteStream = new MediaStream();
    pc.getReceivers().forEach(receiver => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (remoteStream.getTracks().length > 0) {
      this.consultationAudioElement.srcObject = remoteStream;
    }
  }

  disconnectConsultationAudio() {
    if (!this.consultationAudioElement?.srcObject) return;
    this.consultationAudioElement.srcObject.getTracks().forEach(track => track.stop());
    this.consultationAudioElement.srcObject = null;
  }

  cleanupConsultation() {
    this.disconnectConsultationAudio();

    if (this.consultationAudioElement) {
      this.consultationAudioElement.remove();
      this.consultationAudioElement = null;
    }

    this.consultationCall = null;
  }

  // ==================== DTMF ====================

  sendDTMF(tone) {
    if (!this.activeCall) {
      throw new Error('No active call to send DTMF');
    }

    const sessionDescriptionHandler = this.activeCall.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      throw new Error('Session description handler not available');
    }

    sessionDescriptionHandler.sendDtmf(tone);
  }

  // ==================== UTILS ====================

  getCallDuration() {
    if (!this.startTime) return 0;
    return Math.floor((new Date() - this.startTime) / 1000);
  }

  formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
